/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The effective organization decision behind state, which is not always the stored one. Absent rows read as unreviewed, so every tool has a value, and a tool that is not enforceable reads as unreviewed however it was decided — the stored decision is left untouched and takes effect again if the tool gains a verifiable gateway matcher.
 */
export const AIToolAccessSummaryDecision = {
  Unreviewed: "unreviewed",
  Approved: "approved",
  Blocked: "blocked",
} as const;
/**
 * The effective organization decision behind state, which is not always the stored one. Absent rows read as unreviewed, so every tool has a value, and a tool that is not enforceable reads as unreviewed however it was decided — the stored decision is left untouched and takes effect again if the tool gains a verifiable gateway matcher.
 */
export type AIToolAccessSummaryDecision = ClosedEnum<
  typeof AIToolAccessSummaryDecision
>;

/**
 * The effective decision as a table renders it: allowed and blocked mean an organization decision is recorded and in force, unreviewed means nothing is enforced — either because nobody has decided or because the tool is not enforceable, in which case a stored decision is projected as unreviewed. Read enforceable to tell the two apart.
 */
export const AIToolAccessSummaryState = {
  Allowed: "allowed",
  Blocked: "blocked",
  Unreviewed: "unreviewed",
} as const;
/**
 * The effective decision as a table renders it: allowed and blocked mean an organization decision is recorded and in force, unreviewed means nothing is enforced — either because nobody has decided or because the tool is not enforceable, in which case a stored decision is projected as unreviewed. Read enforceable to tell the two apart.
 */
export type AIToolAccessSummaryState = ClosedEnum<
  typeof AIToolAccessSummaryState
>;

/**
 * The enforcement verdict for one AI tool, computed server-side so a client renders wording without re-deriving it. state shares its vocabulary with the shadow MCP inventory's access summary, so one column describes both halves of the Shadow AI section.
 */
export type AIToolAccessSummary = {
  /**
   * The effective organization decision behind state, which is not always the stored one. Absent rows read as unreviewed, so every tool has a value, and a tool that is not enforceable reads as unreviewed however it was decided — the stored decision is left untouched and takes effect again if the tool gains a verifiable gateway matcher.
   */
  decision: AIToolAccessSummaryDecision;
  /**
   * Whether a block on this tool would actually reach the gateway. False when the tool is linked only by a self-reported client name, or by nothing at all: the decision is recorded honestly and enforces nothing. Surfaced so an admin is never told a tool is blocked when it is not.
   */
  enforceable: boolean;
  /**
   * Why the decision was made, when an admin gave a reason. Who recorded it and when are in the audit log rather than here, so there is one record of that and not two.
   */
  rationale?: string | undefined;
  /**
   * The effective decision as a table renders it: allowed and blocked mean an organization decision is recorded and in force, unreviewed means nothing is enforced — either because nobody has decided or because the tool is not enforceable, in which case a stored decision is projected as unreviewed. Read enforceable to tell the two apart.
   */
  state: AIToolAccessSummaryState;
};

/** @internal */
export const AIToolAccessSummaryDecision$inboundSchema: z.ZodMiniEnum<
  typeof AIToolAccessSummaryDecision
> = z.enum(AIToolAccessSummaryDecision);

/** @internal */
export const AIToolAccessSummaryState$inboundSchema: z.ZodMiniEnum<
  typeof AIToolAccessSummaryState
> = z.enum(AIToolAccessSummaryState);

/** @internal */
export const AIToolAccessSummary$inboundSchema: z.ZodMiniType<
  AIToolAccessSummary,
  unknown
> = z.object({
  decision: AIToolAccessSummaryDecision$inboundSchema,
  enforceable: z.boolean(),
  rationale: z.optional(z.string()),
  state: AIToolAccessSummaryState$inboundSchema,
});

export function aiToolAccessSummaryFromJSON(
  jsonString: string,
): SafeParseResult<AIToolAccessSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AIToolAccessSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AIToolAccessSummary' from JSON`,
  );
}
